import { ReactNode } from 'react';
import { parseIngredients } from './helpers/parseIngredients';
import { SimpleList } from '../SimpleList';
import { RecipeProducts } from '../RecipeProducts';
import { DictionaryService, type DictionaryItem } from 'service/dictionary/dictionaryService';
import { Text as Paragraph, Title } from '../Typography';
import clsx from 'clsx';
import { Ingredient } from './components/Ingredient';

export type IngredientType = {
  ArticleSummaryText: string;
  AmountAndUnitSummaryText: string;
  ArticleUnit?: {
    Article?: {
      ProductLink?: string;
      ProductImage?: string;
    };
  };
};

type EquipmentItem = {
  Id: string;
  Title: string;
  TitlePlural: string;
};

type VisibleEquipmentItemPositions = {
  AdditionalText: string;
  EquipmentItem: EquipmentItem;
  Id: string;
  IsAmountVague: boolean;
  SummaryText: string;
};

type IngredientsAndEquipmentBlocks = {
  Title: string;
  Ingredients: IngredientType[];
  Id: string;
  VisibleEquipmentItemPositions: VisibleEquipmentItemPositions[];
};

export type Block = {
  IngredientsAndEquipmentBlocks: IngredientsAndEquipmentBlocks[];
};

interface IIngredientsAndEquipmentProps {
  block: Block;
  copy?: DictionaryItem[];
  trackingEvent?: (data: any) => void;
  hideTabsOnMobile: boolean;
}

export const IngredientsAndEquipmentBlock = ({
  block,
  copy,
  trackingEvent,
  hideTabsOnMobile,
}: IIngredientsAndEquipmentProps): ReactNode => {
  const parsedBlock = parseIngredients(block);

  const equipmentTitle =
    block.IngredientsAndEquipmentBlocks.length > 0
      ? block.IngredientsAndEquipmentBlocks[0].Title
      : DictionaryService.getCopy('equipment', copy);

  const equipmentData = block.IngredientsAndEquipmentBlocks.flatMap((blockItem) =>
    blockItem.VisibleEquipmentItemPositions.map((position) => ({
      title: equipmentTitle,
      list: [position.EquipmentItem.Title],
      additionalText: position.AdditionalText || '',
    })),
  );

  return (
    <>
      <div className="py-4 mb-4">
        <SimpleList title={equipmentTitle} data={equipmentData} />
        <div className="ingredientWrapper mb-12 block">
          <Title
            className={clsx(
              'mb-9 print:block print:overflow-visible print:h-auto print:max-h-none',
              hideTabsOnMobile ? '' : 'hidden md:block',
            )}
            tag="h2"
            type="md"
          >
            {DictionaryService.getCopy('ingredients', copy)}
          </Title>
          {parsedBlock.map((blockitem) => (
            <div key={blockitem.title}>
              <Paragraph
                tag="div"
                type="lead"
                className="ingredientText mb-2 mt-10 text-gray-500 print:break-inside-avoid print:relative"
              >
                {blockitem?.title}
              </Paragraph>
              {blockitem?.ingredients?.map((ingredient, index) => (
                <Ingredient
                  ingredient={ingredient}
                  trackingEvent={trackingEvent}
                  key={`${ingredient}-${index}`}
                />
              ))}
            </div>
          ))}
        </div>
        <div className={hideTabsOnMobile ? 'hidden md:block' : 'block'}>
          <RecipeProducts block={block} copy={copy} trackingEvent={trackingEvent} />
        </div>
      </div>
    </>
  );
};
